import { useCallback, useMemo } from "react";
import {
   ContentModeType,
   ContentPanelActionType,
   FMAppContentPanel,
   FMPanelNote,
   markdownRenderer,
} from "@fokused-me/web-ui";
import { List, Set } from "immutable";
import { INotePanelInfo } from "../../../state/Model";
import { useDesktop } from "../../../state/Desktop";
import { IContentPanelProps } from "./ContentPanelSelector";
import { newNotePanel, useBreadcrumbs, usePanelNodeDropped } from "./Helpers";
import { highlightSearchText } from "@fokused-me/core";

export interface INoteContentPanelProps extends IContentPanelProps {
   panelInfo: INotePanelInfo;
}

export const NoteContentPanel = ({ notepad, panelInfo, notepadState }: INoteContentPanelProps) => {
   const { closePanels, setPanelType, addPanel, toggleExpandPanel, toggleSearchEnabled, setSearchText } = useDesktop();

   /* eslint-disable-next-line */
   const note = useMemo(() => notepad?.getNote(panelInfo.rootNoteId), [notepad, panelInfo.rootNoteId, notepadState]);

   const preparedText = useMemo(
      () =>
         highlightSearchText(
            markdownRenderer.render(note ? `# ${note.title || ""}\n${note.text || ""}` : ""),
            panelInfo.searchText
         ).content,
      [note, panelInfo.searchText]
   );

   const handleModeChange = useCallback(
      (mode: ContentModeType) => setPanelType(panelInfo.id, mode),
      [panelInfo.id, setPanelType]
   );

   const breadcrumbs = useBreadcrumbs(panelInfo, notepad);

   const handlePanelAction = useCallback(
      (action: ContentPanelActionType) => {
         switch (action) {
            case "expand":
               toggleExpandPanel(panelInfo.id);
               break;
            case "close":
               closePanels(List([panelInfo.id]));
               break;
            case "edit":
               setPanelType(panelInfo.id, "edit");
               break;
            case "add":
               newNotePanel(panelInfo, addPanel);
               break;
            case "search":
               toggleSearchEnabled(panelInfo.id);
               break;
         }
      },
      [panelInfo, closePanels, setPanelType, addPanel, toggleExpandPanel, toggleSearchEnabled]
   );

   const actions = useMemo(() => Set.of<ContentPanelActionType>("add", "edit", "search"), []);

   const highlightedActions = useMemo(
      () => (panelInfo.searchEnabled ? Set.of<ContentPanelActionType>("search") : Set()),
      [panelInfo.searchEnabled]
   );

   const handlePanelNodeDropped = usePanelNodeDropped(panelInfo.notepadId, panelInfo.rootNoteId);

   const handleSearchTextChange = useCallback(
      (searchText) => setSearchText(panelInfo.id, searchText),
      [panelInfo.id, setSearchText]
   );

   return (
      <FMAppContentPanel
         expanded={panelInfo.expanded}
         actions={actions}
         disableActions={Set()}
         highlightedActions={highlightedActions}
         disableModes={Set()}
         mode={panelInfo.type}
         breadcrumbs={breadcrumbs}
         searchText={panelInfo.searchText}
         onAction={handlePanelAction}
         onModeChange={handleModeChange}
         onBreadcrumbsNavigate={console.log}
         onSearchTextChange={handleSearchTextChange}
      >
         <FMPanelNote
            text={preparedText}
            notepadId={panelInfo.notepadId}
            noteId={panelInfo.rootNoteId}
            onNodeDropped={handlePanelNodeDropped}
         />
      </FMAppContentPanel>
   );
};
